import type { ReactNode } from "react"
import { memo, useEffect, useMemo, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconCheckOutline18 as CheckIcon,
	IconChevronDownOutline18 as ChevronDownIcon,
} from "nucleo-ui-outline-18"
import { useClickAway, useLocalStorage } from "react-use"
import { z } from "zod"

import type {
	Issue,
	IssueTranslation,
	Project,
	ProjectTranslation,
} from "@productlane/zero"
import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	Form,
	FormTextarea,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { IssueWithLinearRelations } from "@/screens/app/issues/issue-linear-properties"
import type { ProjectWithLinearRelations } from "@/screens/app/projects/project-linear-properties"
import type { Language } from "@/screens/settings/settings-pages/language-selector"
import { useShortcut } from "@/lib"
import {
	useIssueTranslations,
	useProjectTranslations,
} from "@/lib/use-item-translations"
import { useRole } from "@/lib/useRole"
import { useSavableForm } from "@/lib/useSavableForm"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { IssueLinearProperties } from "@/screens/app/issues/issue-linear-properties"
import { IssuePrioritySelect } from "@/screens/app/issues/issue-priority-select"
import { IssueStatusSelect } from "@/screens/app/issues/issue-status-select"
import { ProjectIconSelect } from "@/screens/app/projects/project-icon-select"
import {
	ProjectDatesProperty,
	ProjectLabels,
	ProjectMembersProperty,
	ProjectMilestones,
	ProjectTeamsProperty,
	useHasProjectLabels,
} from "@/screens/app/projects/project-linear-extras"
import { ProjectLinearProperties } from "@/screens/app/projects/project-linear-properties"
import { ProjectSelect } from "@/screens/app/projects/project-select"
import { ProjectStatusSelect } from "@/screens/app/projects/project-status-select"
import { PropertyPills } from "../property-trigger"
import { toast } from "../toast"
import { changedLinearFields } from "./changed-linear-fields"
import { EarlyAccessToggle } from "./early-access-toggle"
import { MarkdownDescriptionField } from "./markdown-description-field"
import { PlainDescriptionField } from "./plain-description-field"

export type TitleTab = "linear" | "portal"

// The Productlane title only shows on the public feature requests page, so without it every item edits its Linear title.
export const useTitleTab = (hasPublicFeatureRequests: boolean) => {
	const [stored, setTab] = useLocalStorage<TitleTab>(
		"description-switch-tab",
		"linear",
	)
	return [
		hasPublicFeatureRequests ? (stored ?? "linear") : "linear",
		setTab,
	] as const
}

const TITLE_TAB_OPTIONS: {
	value: TitleTab
	label: string
}[] = [
	{ value: "linear", label: "Linear title" },
	{ value: "portal", label: "Productlane title" },
]

// A textarea so a long title wraps; Enter stays blocked because titles are one line.
export const TitleField = memo(
	({
		name,
		placeholder,
		readOnly,
	}: {
		name: string
		placeholder: string
		readOnly: boolean
	}) => (
		<FormTextarea
			name={name}
			placeholder={placeholder}
			readOnly={readOnly}
			minRows={1}
			variant="borderless"
			textSize="xl"
			inputClassName="!text-3xl !font-semibold pr-3 text-primary"
			onKeyDown={(event) => {
				if (event.key === "Enter") event.preventDefault()
			}}
		/>
	),
)

export const TitleTabsSwitcher = memo(
	({
		tab,
		onTabChange,
	}: {
		tab: TitleTab
		onTabChange: (tab: TitleTab) => void
	}) => {
		const active = TITLE_TAB_OPTIONS.find((o) => o.value === tab)
		return (
			<DropdownMenu
				trigger={
					// Reads as the last crumb of the breadcrumb it sits in, so it matches
					// the crumbs' color and weight instead of the ghost button's.
					<Button
						variant="ghost"
						iconRight={ChevronDownIcon}
						className="text-sm font-medium text-primary"
					>
						{active?.label}
					</Button>
				}
				contentProps={{ side: "bottom", align: "start" }}
			>
				{TITLE_TAB_OPTIONS.map((option) => (
					<DropdownMenuItem
						key={option.value}
						onClick={() => onTabChange(option.value)}
						shortcut={
							option.value === tab ? <CheckIcon className="h-4 w-4" /> : null
						}
					>
						{option.label}
					</DropdownMenuItem>
				))}
			</DropdownMenu>
		)
	},
)

type DescriptionSwitchProps =
	| {
			type: "project"
			publicDescription: string | null
			publicTitle: string | null
			id: string | undefined
			item: ProjectWithLinearRelations & {
				translations?: readonly ProjectTranslation[]
			}
			tab: TitleTab
			selectedLanguage: Language
	  }
	| {
			type: "issue"
			publicDescription: string | null
			publicTitle: string | null
			id: string | undefined
			item: IssueWithLinearRelations & {
				translations?: readonly IssueTranslation[]
			}
			tab: TitleTab
			selectedLanguage: Language
	  }

const STALE_ECHO_WINDOW_MS = 60_000

const portalFormSchema = z.object({
	publicTitle: z.string().optional(),
	publicDescription: z.string().optional(),
})

type PortalFormData = z.infer<typeof portalFormSchema>

const linearFormSchema = z.object({
	title: z.string().optional(),
	description: z.string().optional(),
})

type LinearFormData = z.infer<typeof linearFormSchema>

// Linear's detail layout: a fixed label column, then chips that wrap under each other.
const PropertiesRow = ({
	children,
	label = "Properties",
}: {
	children: ReactNode
	label?: string
}) => (
	<div className="mb-2 mt-4 flex items-start gap-3">
		<div className="flex h-7 w-20 flex-none items-center text-sm text-secondary">
			{label}
		</div>
		<div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
			{children}
		</div>
	</div>
)

export const DescriptionSwitch = memo(
	({ id, type, item, tab, selectedLanguage }: DescriptionSwitchProps) => {
		const zero = useZero()
		const workspace = useZeroWorkspace()
		const ofType = type === "issue" ? "issue" : "project"
		const itemTitle = type === "project" ? item.name : item.title

		const linearProjectId =
			ofType === "issue" ? (item as Issue).projectId : null

		const [parentProject] = useQuery(
			queries.projectByLinearProjectId({
				linearProjectId: linearProjectId ?? "",
			}),
		)

		const { role } = useRole()
		const descriptionSwitchRef = useRef(null)
		const prevLanguageRef = useRef(selectedLanguage.code)
		const prevItemIdRef = useRef(id)

		const selectedLanguageCode = selectedLanguage.code

		const projectTranslationResult = useProjectTranslations(
			type === "project"
				? (item as Project & { translations?: readonly ProjectTranslation[] })
				: ({
						id: "",
						workspaceId: "",
						translations: [],
					} as unknown as Project & {
						translations?: readonly ProjectTranslation[]
					}),
			selectedLanguage,
		)

		const issueTranslationResult = useIssueTranslations(
			type === "issue"
				? (item as Issue & { translations?: readonly IssueTranslation[] })
				: ({
						id: "",
						workspaceId: "",
						translations: [],
					} as unknown as Issue & {
						translations?: readonly IssueTranslation[]
					}),
			selectedLanguage,
		)

		const defaultLanguage =
			type === "project"
				? projectTranslationResult.defaultLanguage
				: issueTranslationResult.defaultLanguage

		const translatedTitle = useMemo((): string => {
			if (type === "issue") {
				const issueTranslation = issueTranslationResult.selectedTranslation
				return issueTranslation?.title ?? ""
			}
			const projectTranslation = projectTranslationResult.selectedTranslation
			return projectTranslation?.name ?? ""
		}, [
			type,
			issueTranslationResult.selectedTranslation,
			projectTranslationResult.selectedTranslation,
		])

		const translatedDescription = useMemo((): string => {
			if (type === "issue") {
				const issueTranslation = issueTranslationResult.selectedTranslation
				return issueTranslation?.description ?? ""
			}
			const projectTranslation = projectTranslationResult.selectedTranslation
			return projectTranslation?.description ?? ""
		}, [
			type,
			issueTranslationResult.selectedTranslation,
			projectTranslationResult.selectedTranslation,
		])

		const { form: portalForm, save: savePortalForm } =
			useSavableForm<PortalFormData>({
				schema: portalFormSchema,
				defaultValues: {
					publicDescription: translatedDescription,
					publicTitle: translatedTitle,
				},
				saveFunc: async (formValues) => {
					if (!type || !id) {
						throw new Error("Type or id is missing")
					}

					const newBody = {
						id,
						...formValues,
					}
					if (type === "issue") {
						const issueTranslation = issueTranslationResult.selectedTranslation
						const translationId = issueTranslation?.id?.startsWith("temp-")
							? `tmp-${Date.now()}`
							: (issueTranslation?.id ?? `tmp-${Date.now()}`)

						await mutateOk(
							zero.mutate(
								mutators.issueTranslations.upsert({
									id: translationId,
									language: selectedLanguageCode,
									issueId: id,
									title: formValues.publicTitle,
									description: formValues.publicDescription,
									workspaceId: workspace!.id,
								}),
							),
						)

						const shouldUpdateMainIssue =
							selectedLanguageCode === defaultLanguage ||
							!item.publicTitle ||
							!item.publicDescription

						if (shouldUpdateMainIssue) {
							await mutateOk(zero.mutate(mutators.issues.update(newBody)))
						}
					} else {
						const projectTranslation =
							projectTranslationResult.selectedTranslation
						const translationId = projectTranslation?.id?.startsWith("temp-")
							? `tmp-${Date.now()}`
							: (projectTranslation?.id ?? `tmp-${Date.now()}`)

						await mutateOk(
							zero.mutate(
								mutators.projectTranslations.upsert({
									id: translationId,
									language: selectedLanguageCode,
									projectId: id,
									name: formValues.publicTitle,
									description: formValues.publicDescription,
									workspaceId: workspace!.id,
								}),
							),
						)

						const shouldUpdateMainProject =
							selectedLanguageCode === defaultLanguage ||
							!item.publicTitle ||
							!item.publicDescription

						if (shouldUpdateMainProject) {
							await mutateOk(zero.mutate(mutators.projects.update(newBody)))
						}
					}
					return formValues
				},
				onSuccess: (data, { old }) => {
					const isPublicDescriptionChanged =
						data.publicDescription !== old.publicDescription
					const isPublicTitleChanged = data.publicTitle !== old.publicTitle

					if (!isPublicDescriptionChanged && !isPublicTitleChanged) {
						return
					}
					toast.success({
						title: `Updated public ${isPublicDescriptionChanged ? "description" : ""} ${isPublicDescriptionChanged && isPublicTitleChanged ? "and" : ""} ${isPublicTitleChanged ? "title" : ""} for ${type}`,
					})
				},
				onFail: (_, error) => {
					console.error("Failed to update public description or title", error)
				},
				autoSave: true,
			})

		const lastLocalWriteRef = useRef<{
			description: string
			title: string
			at: number
		} | null>(null)

		const { form: linearForm, save: saveLinearForm } =
			useSavableForm<LinearFormData>({
				schema: linearFormSchema,
				defaultValues: {
					description: item.description || "",
					title: itemTitle || "",
				},
				saveFunc: async (formValues) => {
					if (!type || !id) {
						throw new Error("Type or id is missing")
					}

					const { title, description } = changedLinearFields({
						nextTitle: formValues.title,
						nextDescription: formValues.description,
						currentTitle: itemTitle,
						currentDescription: item.description,
					})
					if (title === undefined && description === undefined) {
						return formValues
					}

					if (type === "issue") {
						await mutateOk(
							zero.mutate(
								mutators.issues.update({
									id,
									...(title !== undefined && { title }),
									...(description !== undefined && { description }),
								}),
							),
						)
					} else {
						await mutateOk(
							zero.mutate(
								mutators.projects.update({
									id,
									...(title !== undefined && { name: title }),
									...(description !== undefined && { description }),
								}),
							),
						)
					}
					lastLocalWriteRef.current = {
						description: formValues.description ?? "",
						title: formValues.title ?? "",
						at: Date.now(),
					}
					return formValues
				},
				onFail: (_, error) => {
					console.error("Failed to update Linear description or title", error)
				},
				autoSave: true,
			})

		const updateIssueProject = async (linearProjectId: string | null) => {
			if (type !== "issue") {
				console.error("updateIssueProject called on non-issue type")
			}

			await mutateOk(
				zero.mutate(
					mutators.issues.update({
						id: item.id,
						projectId: linearProjectId,
					}),
				),
			)
		}

		useEffect(() => {
			const languageChanged = prevLanguageRef.current !== selectedLanguageCode
			const itemChanged = prevItemIdRef.current !== id

			if (languageChanged || itemChanged) {
				prevLanguageRef.current = selectedLanguageCode
				prevItemIdRef.current = id
				portalForm.reset({
					publicDescription: translatedDescription,
					publicTitle: translatedTitle,
				})
			}
		}, [
			id,
			selectedLanguageCode,
			portalForm,
			translatedTitle,
			translatedDescription,
		])

		useEffect(() => {
			if (linearForm.formState.isDirty) return

			const local = lastLocalWriteRef.current
			const isOurOwnItem = prevItemIdRef.current === id
			if (
				local &&
				isOurOwnItem &&
				Date.now() - local.at < STALE_ECHO_WINDOW_MS &&
				((item.description ?? "") !== local.description ||
					(itemTitle ?? "") !== local.title)
			) {
				return
			}

			linearForm.reset({
				description: item.description ?? "",
				title: itemTitle ?? "",
			})
		}, [id, item.description, itemTitle, linearForm])

		useShortcut(
			"meta+enter, ctrl+enter",
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			async () => {
				if (tab === "linear") {
					await saveLinearForm()
				} else {
					await savePortalForm()
				}
			},
			{
				enableOnFormTags: true,
				enabled: role !== "VIEWER",
			},
		)
		useClickAway(descriptionSwitchRef, () => {
			void (async () => {
				if (tab === "linear") {
					await saveLinearForm()
				} else {
					await savePortalForm()
				}
			})()
		})

		const hasProjectLabels = useHasProjectLabels(
			type === "project" ? item.linearProjectId : null,
		)
		return (
			<div className="md:px-5 md:py-4" ref={descriptionSwitchRef}>
				<div className="flex min-h-7 flex-row items-start justify-between gap-4 px-1">
					<div className="w-full">
						<div>
							{tab === "portal" ? (
								<div className="mt-6 flex min-h-12 w-full flex-col">
									{type === "project" && (
										<div className="mb-4">
											<ProjectIconSelect
												project={item}
												iconSize="xl"
												withBackground={true}
											/>
										</div>
									)}
									<Form
										form={portalForm}
										className="relative w-full"
										onSubmit={async () => savePortalForm()}
									>
										<TitleField
											key="publicTitle"
											name="publicTitle"
											placeholder={itemTitle || "Portal title"}
											readOnly={role === "VIEWER"}
										/>
										{/* Keyed so the tab switch builds a new editor rather than
										    handing the portal field the Linear document. */}
										<MarkdownDescriptionField
											key="publicDescription"
											name="publicDescription"
											placeholder={`Public description of this ${ofType} that will show up in your portal.`}
											readOnly={role === "VIEWER"}
										/>
									</Form>
								</div>
							) : (
								<div className="mt-6 flex min-h-12 w-full flex-col">
									{type === "project" && (
										<div className="mb-4">
											<ProjectIconSelect
												project={item}
												iconSize="xl"
												withBackground={true}
											/>
										</div>
									)}
									<Form
										form={linearForm}
										className="relative w-full"
										onSubmit={() => saveLinearForm()}
									>
										<TitleField
											key="title"
											name="title"
											placeholder={`Linear ${ofType} title`}
											readOnly={role === "VIEWER"}
										/>
										{type === "project" ? (
											<PlainDescriptionField
												name="description"
												placeholder={`Description of this Linear ${ofType}.`}
												readOnly={role === "VIEWER"}
											/>
										) : (
											<MarkdownDescriptionField
												key="description"
												name="description"
												placeholder={`Description of this Linear ${ofType}.`}
												readOnly={role === "VIEWER"}
											/>
										)}
									</Form>
								</div>
							)}
						</div>

						{type === "project" ? (
							<PropertyPills value={true}>
								<PropertiesRow>
									<ProjectStatusSelect project={item} withStatus />
									<ProjectLinearProperties project={item} />
									<ProjectMembersProperty project={item} />
									<ProjectDatesProperty project={item} />
									<ProjectTeamsProperty project={item} />
									<EarlyAccessToggle project={item} />
								</PropertiesRow>
								{(hasProjectLabels || role !== "VIEWER") && (
									<PropertiesRow label="Labels">
										<ProjectLabels project={item} />
									</PropertiesRow>
								)}
								<ProjectMilestones project={item} />
							</PropertyPills>
						) : (
							<PropertiesRow>
								<IssueStatusSelect issue={item} withStatus />
								<IssuePrioritySelect issue={item} withPriority />
								<ProjectSelect
									asProperty
									projectIds={[]}
									editor={null}
									onChange={async (_, __, linearProjectId) => {
										await updateIssueProject(linearProjectId)
									}}
									side="bottom"
									isHotkeyEnabled={true}
									selectedProject={parentProject}
									modal={false}
									isPopoverPortal={false}
									isDisabled={role === "VIEWER"}
									teamId={(item as Issue).team}
								/>
								<IssueLinearProperties issue={item} />
							</PropertiesRow>
						)}
					</div>
				</div>
			</div>
		)
	},
)
